import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Search, Calendar, User, Eye, ArrowRight, ShieldAlert, FileJson } from 'lucide-react';
import { getAuditLogs, type AuditLog } from '../../services/AuditService';
import { listUsers } from '../../services/UserManagementService';
import { Modal, Button, Card, CardContent } from '../../components/ui';

export default function AuditLogs() {
  const [action, setAction] = useState('');
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [limit, setLimit] = useState(100);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Load logs
  const { data: logs = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['audit-logs', { action, userId, from, to, limit }],
    queryFn: () => getAuditLogs({ action, user_id: userId, from, to, limit }),
    placeholderData: (prev) => prev,
  });

  // Load users for the filter
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: listUsers,
  });

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('es-CO', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <ClipboardList size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-text">Bitácora de Auditoría</h1>
            <p className="text-sm text-text-muted">Registro detallado de acciones críticas e historial del sistema</p>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <Card className="border border-border bg-surface shadow-sm rounded-2xl">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 items-end">
            
            {/* User Filter */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-text-muted">
                Usuario
              </label>
              <div className="relative">
                <select
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  className="w-full rounded-xl border border-border bg-bg/50 px-3 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition duration-150"
                >
                  <option value="">Todos los usuarios</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Action Search */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-text-muted">
                Acción
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Ej: order.cancel, cash.close"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  className="w-full rounded-xl border border-border bg-bg/50 px-3 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition duration-150"
                />
              </div>
            </div>

            {/* Date From */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-text-muted">
                Desde
              </label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full rounded-xl border border-border bg-bg/50 px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition duration-150"
              />
            </div>

            {/* Date To */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-text-muted">
                Hasta
              </label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-full rounded-xl border border-border bg-bg/50 px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition duration-150"
              />
            </div>

            {/* Limit & Clean */}
            <div className="flex gap-2">
              <div className="flex-1 space-y-1.5">
                <label className="block text-xs font-semibold uppercase tracking-wider text-text-muted">
                  Límite
                </label>
                <select
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  className="w-full rounded-xl border border-border bg-bg/50 px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition duration-150"
                >
                  <option value={50}>50 registros</option>
                  <option value={100}>100 registros</option>
                  <option value={200}>200 registros</option>
                  <option value={500}>500 registros</option>
                </select>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setAction('');
                  setUserId('');
                  setFrom('');
                  setTo('');
                  setLimit(100);
                }}
                className="h-[38px] px-3 rounded-xl border border-border text-text hover:bg-muted"
                title="Limpiar Filtros"
              >
                Limpiar
              </Button>
            </div>

          </div>
        </CardContent>
      </Card>

      {/* Logs Table / List */}
      <div className="rounded-2xl border border-border bg-surface shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="h-10 w-10 rounded-full bg-muted shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/4 rounded bg-muted" />
                  <div className="h-3 w-1/2 rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger/10 text-danger">
              <ShieldAlert size={24} />
            </div>
            <div>
              <p className="font-semibold text-text">Error al cargar bitácora</p>
              <p className="text-sm text-text-muted">Asegúrate de contar con los permisos requeridos o vuelve a intentarlo.</p>
            </div>
            <Button onClick={() => refetch()} variant="outline" size="sm">Reintentar</Button>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <ClipboardList className="mx-auto h-12 w-12 text-text-muted/50 mb-3" />
            <p className="text-base font-semibold text-text">No se encontraron registros</p>
            <p className="text-sm text-text-muted max-w-sm mt-1">Intenta ajustando los filtros de búsqueda o el rango de fechas.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-sm text-text">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-6 py-4 font-semibold text-text-muted text-xs uppercase tracking-wider">Fecha / Hora</th>
                  <th className="px-6 py-4 font-semibold text-text-muted text-xs uppercase tracking-wider">Usuario</th>
                  <th className="px-6 py-4 font-semibold text-text-muted text-xs uppercase tracking-wider">Acción</th>
                  <th className="px-6 py-4 font-semibold text-text-muted text-xs uppercase tracking-wider">Descripción</th>
                  <th className="px-6 py-4 font-semibold text-text-muted text-xs uppercase tracking-wider text-right">Detalle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-muted/10 transition-colors">
                    <td className="whitespace-nowrap px-6 py-4 text-xs font-medium text-text-muted">
                      {formatDate(log.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold uppercase">
                          {log.userName?.charAt(0) ?? <User size={12} />}
                        </div>
                        <div>
                          <span className="font-semibold">{log.userName || 'Sistema'}</span>
                          {log.ip && <span className="block text-[10px] text-text-muted">IP: {log.ip}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className="inline-flex items-center rounded-lg bg-secondary/40 px-2.5 py-1 text-xs font-mono font-medium text-text-muted">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-text-muted max-w-md truncate">
                      {log.description}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <button
                        onClick={() => setSelectedLog(log)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-surface text-text hover:bg-muted hover:text-primary transition duration-150"
                        title="Ver metadatos completos"
                      >
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        titleId="audit-detail-modal"
        className="max-w-2xl"
      >
        {selectedLog && (
          <div className="flex flex-col h-full max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <div className="flex items-center gap-2.5">
                <FileJson size={18} className="text-primary" />
                <h3 className="text-lg font-bold text-text">Detalle del Registro #{selectedLog.id}</h3>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-text-muted hover:text-text rounded-lg p-1.5 transition"
              >
                Cerrar
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm bg-muted/20 p-4 rounded-xl border border-border/50">
                <div>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-text-muted">Usuario</span>
                  <span className="font-semibold text-text">{selectedLog.userName || 'Sistema'}</span>
                </div>
                <div>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-text-muted">Fecha / Hora</span>
                  <span className="text-text">{formatDate(selectedLog.createdAt)}</span>
                </div>
                <div>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-text-muted">Acción Realizada</span>
                  <span className="font-mono text-xs font-semibold text-primary">{selectedLog.action}</span>
                </div>
                <div>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-text-muted">IP Origen</span>
                  <span className="text-text font-mono text-xs">{selectedLog.ip || '—'}</span>
                </div>
                {selectedLog.entityType && (
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-text-muted">Tipo Entidad</span>
                    <span className="text-text">{selectedLog.entityType}</span>
                  </div>
                )}
                {selectedLog.entityId && (
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-text-muted">ID Entidad</span>
                    <span className="text-text font-mono text-xs">{selectedLog.entityId}</span>
                  </div>
                )}
              </div>

              <div>
                <span className="block text-[10px] font-bold uppercase tracking-wider text-text-muted mb-1">Descripción de la acción</span>
                <p className="text-sm bg-surface p-3.5 rounded-xl border border-border font-medium text-text leading-relaxed">
                  {selectedLog.description || 'Sin descripción provista.'}
                </p>
              </div>

              {selectedLog.metadata && (
                <div>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-text-muted mb-1">Metadatos del Evento (JSON)</span>
                  <pre className="overflow-x-auto rounded-xl border border-border bg-bg p-4 text-xs font-mono text-text leading-5 select-all no-scrollbar">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 border-t border-border px-6 py-4 bg-muted/10">
              <Button onClick={() => setSelectedLog(null)} variant="outline">
                Cerrar Detalle
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
